// run19-footerformat-qc-17.ts

import { safeAlert, reportResult } from '../runs/common';
import { getConfig } from '../taskpane/configService';

// ─── Types ───────────────────────────────────────────────────────────────────

interface FooterStats {
  paraStyleFixed: number;
  charStyleFixed: number;
  skippedPageRuns: number;
  skippedDelParagraphs: number;
}

const FOOTER_TYPES: { type: Word.HeaderFooterType; name: string }[] = [
  { type: Word.HeaderFooterType.primary,   name: "Primary"   },
  { type: Word.HeaderFooterType.firstPage, name: "FirstPage" },
  { type: Word.HeaderFooterType.evenPages, name: "EvenPages" },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildRStyleReplacements(
  ooxml: string,
  targetStyle: string,
  stats: FooterStats
): { original: string; value: string }[] {
  const rStyleRegex = /<w:rStyle\s+w:val="([^"]+)"/g;
  const seen = new Set<string>();
  const replacements: { original: string; value: string }[] = [];
  let match: RegExpExecArray | null;

  while ((match = rStyleRegex.exec(ooxml)) !== null) {
    const fullMatch = match[0];
    const styleVal  = match[1];

    if (styleVal.toLowerCase().includes("page")) {
      stats.skippedPageRuns++;
      console.log(`    rStyle "${styleVal}" — page-related, preserved`);
      continue;
    }

    if (styleVal === targetStyle) continue;

    if (!seen.has(fullMatch)) {
      seen.add(fullMatch);
      replacements.push({ original: fullMatch, value: styleVal });
    }
  }

  return replacements;
}

async function fixCharacterStyle(
  para: Word.Paragraph,
  ooxml: string,
  targetStyle: string,
  stats: FooterStats,
  context: Word.RequestContext
): Promise<void> {
  const hasPageField = /<w:instrText[^>]*>[^<]*page[^<]*<\/w:instrText>/i.test(ooxml);
  if (hasPageField) {
    console.log(`    Page field paragraph — skipping rStyle changes`);
    stats.skippedPageRuns++;
    return;
  }

  const replacements = buildRStyleReplacements(ooxml, targetStyle, stats);
  if (replacements.length === 0) return;

  let modifiedOoxml = ooxml;
  for (const rep of replacements) {
    modifiedOoxml = modifiedOoxml
      .split(rep.original)
      .join(`<w:rStyle w:val="${targetStyle}"`);
    console.log(`    rStyle "${rep.value}" → "${targetStyle}" ✓`);
  }

  await context.sync();
  para.insertOoxml(modifiedOoxml, "Replace");
  await context.sync();

  context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
  stats.charStyleFixed += replacements.length;
}

async function processParagraph(
  para: Word.Paragraph,
  p: number,
  targetStyle: string,
  stats: FooterStats,
  context: Word.RequestContext
): Promise<void> {
  para.load("text, style");
  await context.sync();

  const paraText     = (para.text  || "").trim();
  const currentStyle = (para.style || "").toLowerCase();
  console.log(`  [P${p + 1}] "${paraText.substring(0, 50)}" | pStyle: "${para.style}"`);

  const ooxmlResult = para.getOoxml();
  await context.sync();
  const ooxml = ooxmlResult.value;

  // Skip paragraphs that have pending tracked deletions
  if (ooxml.includes('<w:del ')) {
    stats.skippedDelParagraphs++;
    console.log(`    ⚠ Paragraph has pending <w:del> blocks — skipped`);
    return;
  }

  // Sub-check 1: paragraph style
  if (currentStyle !== targetStyle.toLowerCase()) {
    para.style = targetStyle;
    await context.sync();
    stats.paraStyleFixed++;
    console.log(`    pStyle → "${targetStyle}" ✓`);
  }

  if (!ooxml.includes('<w:rStyle')) {
    console.log(`    No <w:rStyle> found — skipping character style fix`);
    return;
  }

  await fixCharacterStyle(para, ooxml, targetStyle, stats, context);
}

async function processFooter(
  section: Word.Section,
  sIdx: number,
  ft: { type: Word.HeaderFooterType; name: string },
  targetStyle: string,
  stats: FooterStats,
  context: Word.RequestContext
): Promise<void> {
  let footerBody: Word.Body;
  try {
    footerBody = section.getFooter(ft.type);
    footerBody.load("paragraphs");
    await context.sync();
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : JSON.stringify(e);
    console.log(`  Section ${sIdx + 1} ${ft.name}: No footer — skipped (${msg})`);
    return;
  }

  const paragraphs = footerBody.paragraphs;
  paragraphs.load("items");
  await context.sync();

  if (paragraphs.items.length === 0) {
    console.log(`  Section ${sIdx + 1} ${ft.name}: Empty footer — skipped`);
    return;
  }

  console.log(`\n── Section ${sIdx + 1} ${ft.name} Footer (${paragraphs.items.length} para(s)) ──`);

  for (let p = 0; p < paragraphs.items.length; p++) {
    await processParagraph(paragraphs.items[p], p, targetStyle, stats, context);
  }
}

function buildAlertMessage(stats: FooterStats): string {
  const { paraStyleFixed, charStyleFixed, skippedPageRuns, skippedDelParagraphs } = stats;
  return (
    `Footer Format Check Complete\n\n` +
    (paraStyleFixed       > 0 ? `Paragraph style → Footer: ${paraStyleFixed}\n`              : "") +
    (charStyleFixed       > 0 ? `Character style → Footer: ${charStyleFixed}\n`              : "") +
    (skippedPageRuns      > 0 ? `Page number runs preserved: ${skippedPageRuns}\n`           : "") +
    (skippedDelParagraphs > 0 ? `Paragraphs skipped (pending run21 deletions): ${skippedDelParagraphs}\n` : "") +
    `\n✓ Changes visible in Track Changes`
  );
}

// ─── Entry point ─────────────────────────────────────────────────────────────

export async function run19() {
  const cfg         = getConfig();
  const targetStyle: string = cfg.run19_footer.footerStyle;

  await Word.run(async (context) => {
    try {
      console.log("═".repeat(70));
      console.log("RUN 19: Footer Format Check");
      console.log("═".repeat(70));

      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const sections = context.document.sections;
      sections.load("items");
      await context.sync();
      console.log(`Total sections: ${sections.items.length}\n`);

      const stats: FooterStats = {
        paraStyleFixed: 0, charStyleFixed: 0,
        skippedPageRuns: 0, skippedDelParagraphs: 0,
      };

      for (let s = 0; s < sections.items.length; s++) {
        for (const ft of FOOTER_TYPES) {
          await processFooter(sections.items[s], s, ft, targetStyle, stats, context);
        }
      }

      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();

      const totalFixes = stats.paraStyleFixed + stats.charStyleFixed;
      console.log("\n" + "═".repeat(70));
      console.log(`Paragraph style fixed: ${stats.paraStyleFixed}`);
      console.log(`Character style fixed: ${stats.charStyleFixed}`);
      console.log(`Page number runs preserved: ${stats.skippedPageRuns}`);
      console.log(`Paragraphs skipped (pending deletions): ${stats.skippedDelParagraphs}`);
      console.log(`Total fixes: ${totalFixes}`);
      console.log("✓ Footer Format check complete!");
      console.log("═".repeat(70));

      if (totalFixes > 0) {
        safeAlert(buildAlertMessage(stats));
      } else {
        reportResult('Footer Format — no changes needed', [
          { label: 'All footers already correctly formatted', note: 'No changes made' },
        ]);
      }

    } catch (error) {
      console.error("Error in run19:", error);
      console.error("Message:", (error as Error).message);
      safeAlert("An error occurred during Footer Format check.\nCheck console (F12) for details.");
    }
  });
}